import { useEffect, useState } from 'react'
import { Ban, CheckCircle2, LoaderCircle, Play, ShieldCheck, XCircle } from 'lucide-react'

import { runtimeApi } from '@/api/ideApi'

const TERMINAL_STATES = new Set(['SUCCEEDED', 'FAILED', 'TIMED_OUT', 'CANCELLED'])

function StateIcon({ state }) {
  if (state === 'SUCCEEDED') return <CheckCircle2 size={15} />
  if (TERMINAL_STATES.has(state)) return <XCircle size={15} />
  return <LoaderCircle className="runner-spin" size={15} />
}

export function GovernedRunnerPanel({ runtime }) {
  const [profile, setProfile] = useState('PYTHON_TEST')
  const [active, setActive] = useState(null)
  const [error, setError] = useState('')
  const [starting, setStarting] = useState(false)

  useEffect(() => {
    if (!active || TERMINAL_STATES.has(active.job.state)) return undefined
    let cancelled = false
    const poll = async () => {
      try {
        const next = await runtimeApi.getRunner(active.runId, active.job.id)
        if (!cancelled && next) {
          setActive(next)
          setError('')
        }
      } catch (pollError) {
        if (!cancelled) setError(String(pollError?.message || 'Runner status is temporarily unavailable.'))
      }
    }
    const intervalId = window.setInterval(poll, 2000)
    void poll()
    return () => {
      cancelled = true
      window.clearInterval(intervalId)
    }
  }, [active?.job.id, active?.job.state, active?.runId])

  const start = async () => {
    setStarting(true)
    setError('')
    try {
      const response = await runtimeApi.startRunner(profile)
      if (!response) throw new Error('The isolated runner is unavailable.')
      setActive(response)
    } catch (startError) {
      setError(String(startError?.message || 'Unable to start the isolated job.'))
    } finally {
      setStarting(false)
    }
  }

  const cancel = async () => {
    if (!active) return
    try {
      const response = await runtimeApi.cancelRunner(active.runId, active.job.id)
      if (response) setActive(response)
    } catch (cancelError) {
      setError(String(cancelError?.message || 'Unable to cancel the isolated job.'))
    }
  }

  const busy = starting || (active && !TERMINAL_STATES.has(active.job.state))
  return (
    <section className="governed-runner" aria-label="Isolated build and test runner">
      <header>
        <div>
          <strong><ShieldCheck size={15} /> Governed execution</strong>
          <p>Builds and tests run in an isolated, evidence-producing environment.</p>
        </div>
        {active && <span className={`runner-state runner-state--${active.job.state.toLowerCase()}`}><StateIcon state={active.job.state} /> {active.job.state.replace('_', ' ')}</span>}
      </header>
      <div className="runner-controls">
        <label>
          Approved job
          <select value={profile} onChange={event => setProfile(event.target.value)} disabled={busy}>
            <option value="PYTHON_TEST">Python tests</option>
            <option value="PYTHON_BUILD">Python build</option>
            <option value="NODE_TEST">Node tests</option>
            <option value="NODE_BUILD">Node build</option>
          </select>
        </label>
        <button type="button" onClick={start} disabled={busy}>
          {starting ? <LoaderCircle className="runner-spin" size={14} /> : <Play size={14} />}
          {starting ? 'Preparing…' : 'Run securely'}
        </button>
        {active && !TERMINAL_STATES.has(active.job.state) && (
          <button type="button" className="runner-cancel" onClick={cancel}><Ban size={14} /> Cancel</button>
        )}
      </div>
      <div className="runner-context">
        <span>{runtime?.taskContext?.task_code}</span>
        <span>Plan v{runtime?.taskContext?.plan_version}</span>
        <span>No public network</span>
      </div>
      {error && <div className="runner-error" role="alert">{error}</div>}
      {active && (
        <div className="runner-output" aria-live="polite">
          {(active.job.log_lines || []).length ? active.job.log_lines.map((line, index) => <div key={`${index}-${line}`}>{line}</div>) : <div>Waiting for isolated runner output…</div>}
          {(active.job.artifacts || []).map(artifact => (
            <div className="runner-artifact" key={`${artifact.name}-${artifact.digest}`}>
              Evidence: {artifact.name} · {artifact.digest.slice(0, 12)}…
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
